import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = 'December 23, 2024';

  return (
    <div className="privacy-policy-container">
      <header className="privacy-header">
        <div className="privacy-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
      </header>
      <div className="privacy-policy-content">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </button>

        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            SiteOperations.io ("we," "our," or "us") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your
            information when you use our clinical site management platform.
          </p>
          <p>
            By accessing or using SiteOperations.io, you agree to the terms of this Privacy Policy.
            If you do not agree with the terms, please do not access the platform.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Account Information</h3>
          <p>When you create an account or sign in, we collect:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Authentication credentials (password hash or OAuth tokens)</li>
            <li>Role and access permissions</li>
          </ul>

          <h3>2.2 Clinical Operations Data</h3>
          <p>To provide our services, we process:</p>
          <ul>
            <li>Patient initials and subject IDs (no full patient names or PHI)</li>
            <li>Appointment schedules and status information</li>
            <li>Study names and visit types</li>
            <li>Staff assignments (CRC names, investigator assignments)</li>
            <li>Room assignments and facility information</li>
            <li>Operational notes related to appointments</li>
          </ul>

          <h3>2.3 Usage Information</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>Log data (IP address, browser type, access times)</li>
            <li>Device information</li>
            <li>Feature usage patterns</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Authenticate users and manage access permissions</li>
            <li>Enable real-time status tracking and notifications</li>
            <li>Facilitate communication between clinical site staff</li>
            <li>Generate operational reports and analytics</li>
            <li>Send service-related communications (invitations, alerts)</li>
            <li>Ensure security and prevent unauthorized access</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share information with:</p>

          <h3>4.1 Service Providers</h3>
          <p>We use third-party services to operate our platform:</p>
          <ul>
            <li><strong>Supabase:</strong> Database hosting and authentication</li>
            <li><strong>Google:</strong> OAuth authentication (if you choose to sign in with Google)</li>
            <li><strong>Resend:</strong> Transactional email delivery</li>
            <li><strong>OneSignal:</strong> Push notification services</li>
            <li><strong>Render:</strong> Application hosting</li>
          </ul>

          <h3>4.2 Within Your Organization</h3>
          <p>
            Information is shared with other authorized users within your clinical site
            or network as necessary for operational purposes.
          </p>

          <h3>4.3 Legal Requirements</h3>
          <p>
            We may disclose information if required by law, regulation, legal process,
            or governmental request.
          </p>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect
            your information, including:
          </p>
          <ul>
            <li>Encryption of data in transit (TLS/SSL)</li>
            <li>Encryption of data at rest</li>
            <li>Role-based access controls</li>
            <li>Regular security assessments</li>
            <li>Secure authentication practices</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is
            completely secure. While we strive to protect your information, we cannot
            guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed
            to provide services. Appointment and operational data may be retained for
            compliance and reporting purposes as required by applicable regulations.
          </p>
          <p>
            Upon account deletion or request, we will delete or anonymize your personal
            information within 30 days, except where retention is required by law.
          </p>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Correct inaccurate or incomplete information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to or restrict certain processing</li>
            <li>Data portability</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information provided below.
          </p>
        </section>

        <section>
          <h2>8. HIPAA Compliance</h2>
          <p>
            SiteOperations.io is designed to support clinical research operations. The platform
            is configured to minimize the collection of Protected Health Information (PHI).
            We use patient initials and subject IDs rather than full patient identifiers.
          </p>
          <p>
            If your organization requires a Business Associate Agreement (BAA), please
            contact us to discuss your compliance requirements.
          </p>
        </section>

        <section>
          <h2>9. Cookies and Tracking</h2>
          <p>
            We use essential cookies and local storage to maintain your session and
            preferences. We do not use third-party advertising or tracking cookies.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under 18 years of age. We do not
            knowingly collect personal information from children.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of
            any material changes by posting the new policy on this page and updating the
            "Last Updated" date.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices,
            please contact us at:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> privacy@siteoperations.io<br />
            <strong>Website:</strong> https://siteoperations.io
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
